/**
 * Authentic Mumbai Real Estate Cover Image Engine.
 *
 * Provides authentic, high-resolution imagery specifically depicting real Mumbai landmarks:
 * - Bandra-Worli Sea Link and Western Suburbs coastline
 * - Marine Drive Queen's Necklace at dusk
 * - Bandra Kurla Complex (BKC) modern commercial skyscrapers
 * - High-end Mumbai high-rise penthouses with Arabian Sea views
 * - Active Mumbai redevelopment construction sites with tower cranes
 * - Authentic Indian property registration documents, non-judicial stamp papers & ₹500 notes
 *
 * Deterministic hash-based selection ensures variety across articles sharing a category.
 */

// 1. Bandra, Khar & Western Suburbs Coastal (Sea Link, Arabian Sea, Carter Road)
const MUMBAI_SEALINK_AND_COASTAL = [
  "/blog-covers/mumbai-sealink-skyline.jpg", // Iconic Bandra-Worli Sea Link & Western Suburbs golden hour
  "https://images.unsplash.com/photo-1569758267239-d08deb78bb1a?auto=format&fit=crop&w=1200&q=80", // Bandra-Worli Sea Link daylight by Previn Samuel
  "https://images.unsplash.com/photo-1708358121090-55d0faf76d31?auto=format&fit=crop&w=1200&q=80", // Mumbai coastal road & western coastline aerial
];

// 2. South Mumbai, Worli, Lower Parel Skylines & MTHL
const MUMBAI_WORLI_SOUTH_SKYLINE = [
  "/blog-covers/mumbai-marine-drive-dusk.jpg", // Marine Drive Queen's Necklace twilight with Art Deco promenade
  "https://images.unsplash.com/photo-1570168007204-dfb528c6958f?auto=format&fit=crop&w=1200&q=80", // Marine Drive dusk curve
  "https://images.unsplash.com/photo-1566552881560-0be862a7c445?auto=format&fit=crop&w=1200&q=80", // Worli / Lower Parel skyline towers at sunset
  "https://images.unsplash.com/photo-1529253355930-ddbe423a2ac7?auto=format&fit=crop&w=1200&q=80", // High-rise aerial night skyline Mumbai / MTHL
  "https://images.unsplash.com/photo-1582510003544-4d00b7f74220?auto=format&fit=crop&w=1200&q=80", // South Mumbai heritage & Colaba waterfront
];

// 3. BKC (Bandra Kurla Complex), Grade-A Commercial & Offices
const MUMBAI_BKC_COMMERCIAL = [
  "/blog-covers/mumbai-bkc-commercial.jpg", // BKC G-Block glass commercial skyscraper & corporate plaza
  "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80", // Modern glass corporate tower
  "https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=1200&q=80", // Commercial office interior
];

// 4. Celebrity Deals & Ultra-Luxury Penthouses (Juhu, Versova, Bandra West)
const MUMBAI_CELEBRITY_LUXURY_PENTHOUSE = [
  "/blog-covers/mumbai-luxury-penthouse-interior.jpg", // Ultra-luxury Mumbai penthouse living room with Arabian Sea views
  "/blog-covers/mumbai-sealink-skyline.jpg", // Bandra West luxury coastline
  "https://images.unsplash.com/photo-1624456735729-03594a40c5fb?auto=format&fit=crop&w=1200&q=80", // Mumbai skyline panorama
  "https://images.unsplash.com/photo-1566552881560-0be862a7c445?auto=format&fit=crop&w=1200&q=80", // Worli luxury residential high-rise
];

// 5. Urban Redevelopment, MHADA, Dharavi & Civic Projects
const MUMBAI_REDEVELOPMENT_CIVIC = [
  "/blog-covers/mumbai-construction-redevelopment.jpg", // Active Mumbai redevelopment high-rise construction site with tower cranes
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1200&q=80", // Architecture blueprints & redevelopment planning
  "https://images.unsplash.com/photo-1515263487990-61b07816b324?auto=format&fit=crop&w=1200&q=80", // Building structural development
];

// 6. Legal, MahaRERA, Tax (ITAT, Stamp Duty, Aadhaar, Conveyance)
const LEGAL_MAHARERA_TAX = [
  "/blog-covers/mumbai-legal-property-documents.jpg", // Indian property registration deed, stamp papers, ₹500 notes & seal
  "https://images.unsplash.com/photo-1450133064473-71024230f91b?auto=format&fit=crop&w=1200&q=80", // Conveyance agreement signing & legal deed
  "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=1200&q=80", // Legal scales & regulatory compliance
];

// 7. Weekend Retreats & Hillside Villas (Lonavala, Khandala, Alibaug)
const MUMBAI_WEEKEND_VILLAS = [
  "/blog-covers/mumbai-luxury-penthouse-interior.jpg",
  "/blog-covers/mumbai-sealink-skyline.jpg",
];

// 8. General Mumbai Residential & Market Benchmark
const MUMBAI_RESIDENTIAL_GENERAL = [
  "/blog-covers/mumbai-sealink-skyline.jpg",
  "/blog-covers/mumbai-marine-drive-dusk.jpg",
  "https://images.unsplash.com/photo-1566552881560-0be862a7c445?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1708358121090-55d0faf76d31?auto=format&fit=crop&w=1200&q=80",
];

export const DEFAULT_MUMBAI_FALLBACK = "/blog-covers/mumbai-sealink-skyline.jpg";

function selectDeterministic(pool: string[], key: string): string {
  if (!pool || pool.length === 0) return DEFAULT_MUMBAI_FALLBACK;
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash |= 0;
  }
  const idx = Math.abs(hash) % pool.length;
  return pool[idx];
}

export function getBlogCoverImage(post?: {
  cover_image_url?: string | null;
  title?: string | null;
  category?: string | null;
  slug?: string | null;
}): string {
  if (post?.cover_image_url && post.cover_image_url.trim()) {
    let url = post.cover_image_url.trim();
    if (url.includes("images.unsplash.com") && !url.includes("fm=webp")) {
      url += (url.includes("?") ? "&" : "?") + "fm=webp";
    }
    return url;
  }

  const rawText = `${post?.title || ""} ${post?.slug || ""} ${post?.category || ""}`;
  const norm = rawText.toLowerCase().replace(/[^a-z0-9]+/g, " ");
  const key = post?.slug || post?.title || "default";

  // 1. Weekend villas & Second homes (Lonavala, Khandala, Alibaug)
  if (/\b(lonavala|khandala|alibaug|farmhouse|villa|second home)\b/.test(norm)) {
    return selectDeterministic(MUMBAI_WEEKEND_VILLAS, key);
  }

  // 2. Commercial / BKC / Office / REIT / Retail / Pre-leased
  if (/\b(bkc|reits?|commercial|office|retail|corporate|pre leased|showroom)\b/.test(norm)) {
    return selectDeterministic(MUMBAI_BKC_COMMERCIAL, key);
  }

  // 3. Legal, Tax, ITAT, MahaRERA, Stamp Duty, Aadhaar, Registrations
  if (/\b(itat|tax|taxes|taxation|stamp|aadhaar|registration|maharera|mahareat|penalty|54f|conveyance|mandate)\b/.test(norm)) {
    return selectDeterministic(LEGAL_MAHARERA_TAX, key);
  }

  // 4. Redevelopment, Slum, MHADA, Dharavi, Rehabilitation, Civic, BMC, Demolition
  if (/\b(dharavi|mhada|redevelopment|rehabilitation|bmc|demolition|demolishes|alterations?|encroachments?|pollution|clean air|chawl)\b/.test(norm)) {
    return selectDeterministic(MUMBAI_REDEVELOPMENT_CIVIC, key);
  }

  // 5. Bandra, Khar, Coastal & Sea Link
  if (/\b(bandra|khar|sea link|bandstand|carter|perry cross|linking road)\b/.test(norm)) {
    return selectDeterministic(MUMBAI_SEALINK_AND_COASTAL, key);
  }

  // 6. South Mumbai, Marine Drive, Worli, Prabhadevi, MTHL & Corridor
  if (/\b(marine lines|marine drive|malabar|worli|prabhadevi|colaba|mthl|corridor|skyline|registrations)\b/.test(norm)) {
    return selectDeterministic(MUMBAI_WORLI_SOUTH_SKYLINE, key);
  }

  // 7. Celebrity Deals, Penthouses & Ultra-Luxury High-Rises
  if (/\b(versova|juhu|parle|chaturvedi|roshan|kohli|anushka|khanna|zinta|kapoor|luxury|penthouse|towers?|high rise|duplex)\b/.test(norm)) {
    return selectDeterministic(MUMBAI_CELEBRITY_LUXURY_PENTHOUSE, key);
  }

  // 8. General Mumbai Residential
  return selectDeterministic(MUMBAI_RESIDENTIAL_GENERAL, key);
}
